import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import {
  SSO_NO_PASSWORD_TO_RESET_COPY,
  isSsoSignInRequired
} from 'src/app/services/vive-api-errors';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ResetPasswordComponent implements OnInit {
  constructor(
    private UserService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private ToastService: ToastService,
    private AuthService: AuthService
  ) {}

  Entry = new FormGroup({
    password: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    passwordConfirm: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    })
  });

  passwordError!: {
    error:
      | 'passwordLength'
      | 'passwordUppercase'
      | 'passwordLowercase'
      | 'passwordSpecialCharacter'
      | 'passwordNumber'
      | 'passwordMatch';
  } | null;

  resetCode!: string;
  isPasswordReset: boolean = false;

  checkForExpiredResetCode() {
    // A code is single-use, and the check cannot tell expired from used.
    this.UserService.verifyPasswordResetCode(this.resetCode).subscribe({
      next: ({ valid }) => {
        if (!valid) {
          this.ToastService.addErrorToast(
            'Error',
            'This password reset link has expired or was already used. Request a new one.',
            { returnError: true }
          );
          this.router.navigate(['login', 'forgot-password']);
        }
      }
    });
  }

  resetPassword() {
    if (this.passwordError) {
      const { error } = this.passwordError;

      if (error === 'passwordLength') {
        throw this.ToastService.addErrorToast(
          'Error',
          'Password length is too short.',
          { returnError: true }
        );
      }

      if (error === 'passwordUppercase') {
        throw this.ToastService.addErrorToast(
          'Error',
          'Password does not contain an uppercase character.',
          { returnError: true }
        );
      }

      if (error === 'passwordLowercase') {
        throw this.ToastService.addErrorToast(
          'Error',
          'Password does not contain a lowercase character.',
          { returnError: true }
        );
      }

      if (error === 'passwordSpecialCharacter') {
        throw this.ToastService.addErrorToast(
          'Error',
          'Password does not contain a special character.',
          { returnError: true }
        );
      }

      if (error === 'passwordNumber') {
        throw this.ToastService.addErrorToast(
          'Error',
          'Password does not contain a number.',
          { returnError: true }
        );
      }

      if (error === 'passwordMatch') {
        throw this.ToastService.addErrorToast(
          'Error',
          'Passwords do not match.',
          { returnError: true }
        );
      }
    }

    const {
      value: { password, passwordConfirm }
    } = this.Entry;

    if (!password) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must enter a new password.',
        { returnError: true }
      );
    }

    if (!passwordConfirm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must confirm your password.',
        { returnError: true }
      );
    }

    this.UserService.confirmPasswordReset({
      code: this.resetCode,
      password
    }).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          'Password was successfully reset.'
        );
        this.isPasswordReset = true;
        this.AuthService.clearSession();
        this.router.navigate(['login']);
      },
      error: (error: unknown) => {
        if (isSsoSignInRequired(error)) {
          this.ToastService.addErrorToast(
            'Error',
            SSO_NO_PASSWORD_TO_RESET_COPY
          );
          return;
        }
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error resetting your password. Please try again.',
          { returnError: true }
        );
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (ParamMap) => {
        const resetCode = ParamMap.get('resetCode');

        if (!resetCode) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Could not find user password to reset.',
            { returnError: true }
          );
        }

        this.resetCode = resetCode;

        this.checkForExpiredResetCode();
      }
    });
  }
}
